import { useState, useEffect, useRef } from 'react'
import { Box, Button, Group, Stack, Table, Text, Progress, ActionIcon, Paper } from '@mantine/core'
import { IconUpload, IconFolderOpen, IconX, IconCheck, IconAlertCircle } from '@tabler/icons-react'
import '@uppy/core/dist/style.min.css'

const AUDIO_EXTENSIONS = ['.mp3', '.wav', '.flac', '.m4a', '.aac', '.ogg', '.opus', '.wma', '.aiff', '.ape']

export default function TunistryUploader({ uploader }) {
    const [files, setFiles] = useState([])
    const [isDragging, setIsDragging] = useState(false)
    const fileInputRef = useRef(null)
    const directoryInputRef = useRef(null)

    useEffect(() => {
        const uppy = uploader.uppy

        const handleFileAdded = (file) => {
            setFiles((prev) => {
                // Check if file already exists
                if (prev.find(f => f.id === file.id)) return prev
                return [...prev, {
                    id: file.id,
                    name: file.name,
                    size: file.size,
                    progress: 0,
                    status: 'waiting', // waiting, uploading, completed, error
                    error: null
                }]
            })
        }

        const handleFileRemoved = (file) => {
            setFiles((prev) => prev.filter(f => f.id !== file.id))
        }

        const handleUploadProgress = (file, progress) => {
            setFiles((prev) => prev.map(f =>
                f.id === file.id
                    ? { ...f, progress: progress.percentage || 0, status: 'uploading' }
                    : f
            ))
        }

        const handleUploadSuccess = (file) => {
            setFiles((prev) => prev.map(f =>
                f.id === file.id
                    ? { ...f, progress: 100, status: 'completed' }
                    : f
            ))
        }

        const handleUploadError = (file, error) => {
            setFiles((prev) => prev.map(f =>
                f.id === file.id
                    ? { ...f, status: 'error', error: error.message }
                    : f
            ))
        }

        const handlePreprocessProgress = (file, progress) => {
            if (progress.message) {
                setFiles((prev) => prev.map(f =>
                    f.id === file.id
                        ? { ...f, status: 'processing', progress: progress.percentage || 0 }
                        : f
                ))
            }
        }

        uppy.on('file-added', handleFileAdded)
        uppy.on('file-removed', handleFileRemoved)
        uppy.on('upload-progress', handleUploadProgress)
        uppy.on('upload-success', handleUploadSuccess)
        uppy.on('upload-error', handleUploadError)
        uppy.on('preprocess-progress', handlePreprocessProgress)

        return () => {
            uppy.off('file-added', handleFileAdded)
            uppy.off('file-removed', handleFileRemoved)
            uppy.off('upload-progress', handleUploadProgress)
            uppy.off('upload-success', handleUploadSuccess)
            uppy.off('upload-error', handleUploadError)
            uppy.off('preprocess-progress', handlePreprocessProgress)
        }
    }, [uploader])

    const isAudioFile = (filename) => {
        const lower = filename.toLowerCase()
        return AUDIO_EXTENSIONS.some(ext => lower.endsWith(ext))
    }

    const handleDragEnter = (e) => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragging(true)
    }

    const handleDragLeave = (e) => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragging(false)
    }

    const handleDragOver = (e) => {
        e.preventDefault()
        e.stopPropagation()
    }

    const handleDrop = async (e) => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragging(false)

        const items = Array.from(e.dataTransfer.items)
        const files = []

        for (const item of items) {
            if (item.kind === 'file') {
                const entry = item.webkitGetAsEntry?.() || item.getAsEntry?.()
                if (entry) {
                    await processEntry(entry, files)
                } else {
                    const file = item.getAsFile()
                    if (file && isAudioFile(file.name)) {
                        files.push(file)
                    }
                }
            }
        }

        files.forEach(file => {
            try {
                uploader.uppy.addFile({
                    name: file.name,
                    type: file.type,
                    data: file,
                })
            } catch (err) {
                console.error('Error adding file:', err)
            }
        })

        if (files.length > 0) {
            uploader.uppy.upload()
        }
    }

    const processEntry = async (entry, files) => {
        if (entry.isFile) {
            return new Promise((resolve) => {
                entry.file((file) => {
                    if (isAudioFile(file.name)) {
                        files.push(file)
                    }
                    resolve()
                })
            })
        } else if (entry.isDirectory) {
            const reader = entry.createReader()
            const entries = await new Promise((resolve) => {
                reader.readEntries((entries) => resolve(entries))
            })
            for (const childEntry of entries) {
                await processEntry(childEntry, files)
            }
        }
    }

    const handleFileSelect = (e) => {
        const selectedFiles = Array.from(e.target.files)
        selectedFiles.forEach(file => {
            if (isAudioFile(file.name)) {
                try {
                    uploader.uppy.addFile({
                        name: file.name,
                        type: file.type,
                        data: file,
                    })
                } catch (err) {
                    console.error('Error adding file:', err)
                }
            }
        })
        if (selectedFiles.length > 0) {
            uploader.uppy.upload()
        }
        e.target.value = '' // Reset input
    }

    const handleDirectorySelect = async (e) => {
        const selectedFiles = Array.from(e.target.files)
        selectedFiles.forEach(file => {
            if (isAudioFile(file.name)) {
                try {
                    uploader.uppy.addFile({
                        name: file.name,
                        type: file.type,
                        data: file,
                    })
                } catch (err) {
                    console.error('Error adding file:', err)
                }
            }
        })
        if (selectedFiles.length > 0) {
            uploader.uppy.upload()
        }
        e.target.value = '' // Reset input
    }

    const handleRemoveFile = (fileId) => {
        uploader.uppy.removeFile(fileId)
    }

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes'
        const k = 1024
        const sizes = ['Bytes', 'KB', 'MB', 'GB']
        const i = Math.floor(Math.log(bytes) / Math.log(k))
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
    }

    const getStatusIcon = (status) => {
        switch (status) {
            case 'completed':
                return <IconCheck size={16} color="green" />
            case 'error':
                return <IconAlertCircle size={16} color="red" />
            default:
                return null
        }
    }

    return (
        <Stack gap="md">
            {/* Hidden file inputs */}
            <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="audio/*"
                style={{ display: 'none' }}
                onChange={handleFileSelect}
            />
            <input
                ref={directoryInputRef}
                type="file"
                {...{ webkitdirectory: '', directory: '' }}
                multiple
                style={{ display: 'none' }}
                onChange={handleDirectorySelect}
            />

            {/* Drop zone */}
            <Paper
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                style={{
                    border: `2px dashed ${isDragging ? '#6366f1' : 'rgba(255,255,255,0.2)'}`,
                    borderRadius: '8px',
                    padding: '3rem 2rem',
                    textAlign: 'center',
                    backgroundColor: isDragging ? 'rgba(99,102,241,0.1)' : 'rgba(0,0,0,0.2)',
                    transition: 'all 0.2s ease',
                    cursor: 'pointer'
                }}
                onClick={() => fileInputRef.current?.click()}
            >
                <Stack align="center" gap="sm">
                    <IconUpload size={48} style={{ opacity: 0.5 }} />
                    <Text size="lg" fw={500}>
                        Drag and drop music files here
                    </Text>
                    <Text size="sm" c="dimmed">
                        or click to browse files
                    </Text>
                </Stack>
            </Paper>

            {/* Action buttons */}
            <Group>
                <Button
                    leftSection={<IconUpload size={16} />}
                    variant="light"
                    onClick={() => fileInputRef.current?.click()}
                >
                    Add Files
                </Button>
                <Button
                    leftSection={<IconFolderOpen size={16} />}
                    variant="light"
                    onClick={() => directoryInputRef.current?.click()}
                >
                    Add Folder
                </Button>
            </Group>

            {/* File list */}
            {files.length > 0 && (
                <Box style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    <Table highlightOnHover>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th>File Name</Table.Th>
                                <Table.Th>Size</Table.Th>
                                <Table.Th>Status</Table.Th>
                                <Table.Th style={{ width: '60px' }}></Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {files.map((file) => (
                                <Table.Tr key={file.id}>
                                    <Table.Td>
                                        <Text size="sm" truncate style={{ maxWidth: '300px' }}>
                                            {file.name}
                                        </Text>
                                        {(file.status === 'uploading' || file.status === 'processing') && (
                                            <Progress
                                                value={file.progress}
                                                size="xs"
                                                mt={4}
                                                color={file.status === 'processing' ? 'yellow' : 'blue'}
                                            />
                                        )}
                                        {file.error && (
                                            <Text size="xs" c="red" mt={4}>
                                                {file.error}
                                            </Text>
                                        )}
                                    </Table.Td>
                                    <Table.Td>
                                        <Text size="sm">{formatFileSize(file.size)}</Text>
                                    </Table.Td>
                                    <Table.Td>
                                        <Group gap="xs">
                                            {getStatusIcon(file.status)}
                                            <Text size="sm" c="dimmed">
                                                {file.status === 'processing' ? 'Processing...' :
                                                 file.status === 'uploading' ? 'Uploading...' :
                                                 file.status === 'completed' ? 'Complete' :
                                                 file.status === 'error' ? 'Error' : 'Waiting'}
                                            </Text>
                                        </Group>
                                    </Table.Td>
                                    <Table.Td>
                                        {file.status !== 'completed' && file.status !== 'uploading' && (
                                            <ActionIcon
                                                color="red"
                                                variant="subtle"
                                                onClick={() => handleRemoveFile(file.id)}
                                            >
                                                <IconX size={16} />
                                            </ActionIcon>
                                        )}
                                    </Table.Td>
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                </Box>
            )}
        </Stack>
    )
}

